import {Box, Link, Typography} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AlignVerticalTopIcon from "@mui/icons-material/AlignVerticalTop";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import AlignVerticalCenterIcon from "@mui/icons-material/AlignVerticalCenter";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {HardwareItem} from "../../../../../../api/Hardware";
import {RibbonButton, RibbonGroup, RibbonSelect} from "../Ribbon";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {doorPlacementViolations, itemFace, PrepKind, standardPosition} from "../../model/hardware/MdHardwareRules";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {useEffect, useReducer, useState} from "react";
import {ProductEditFromId} from "../../../extras/ProductEdit";
import {DoorFace} from "../../scenes/frameProfile/Helpers";

type HardwareSel = Extract<MdSelection, {type: "hardware-item"}>;

// what a door placement's position is linked to: the prep its rule names, or nothing.
type LinkedTo = PrepKind | "none";
const PREP_LABELS: Record<PrepKind, string> = {"161": "161", db: "DB", closer: "Closer"};

// the contextual tab for a placed hardware-item crosshair. The tab shows the item's
// identity, its prep link and offers Reset + Manage. Every group item is on the drawing,
// so there is nothing to remove here.
export function HardwareItemSelection(props: {app: ElevationApp; selection: HardwareSel[]}) {
    const app = props.app;
    const sel = props.selection;
    // re-render when a placement's alignment changes so the buttons reflect it.
    useEventEmitter2(app.hardware.listChanged);
    // the hardware sidebar only exists on the hardware workspace, so Manage is offered there only.
    const workspace = useEventEmitter2(app.viewSettings.workspace);

    // one product may have both a door and a frame crosshair; removing works per
    // product, so dedupe.
    const productIds = Array.from(new Set(sel.map(s => s.productId)));
    const single = productIds.length === 1;
    const item = single ? (app.hardwareGroup?.hardware ?? []).find(h => h.product === productIds[0]) : undefined;

    // the product(s) the item renders as for this opening (its dimensioned codes, e.g. per
    // hand), falling back to the generic product. Each opens the product editor.
    const rendered = item ? renderedProducts(item) : [];
    const [editProduct, setEditProduct] = useState<number | null>(null);

    // the actual placements behind the selection (by crosshair id), whose
    // alignment the buttons below edit.
    const placements = sel
        .map(s => app.hardware.list.find(p => p.id === s.id))
        .filter((p): p is MdHardwarePlacement => !!p);

    // re-render as the selected placements move (drag / dimension edit) so the rule
    // check below tracks them.
    const [, forceRender] = useReducer((x: number) => x + 1, 0);
    const placementKey = placements.map(p => p.id).join(",");
    useEffect(() => {
        const subs = placements.flatMap(p => [p.x.subscribe(() => forceRender()), p.y.subscribe(() => forceRender())]);
        return () => subs.forEach(sub => sub.cancel());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placementKey]);

    const violations = placements.flatMap(p => {
        const hw = (app.hardwareGroup?.hardware ?? []).find(h => h.product === p.productId);
        return hw ? doorPlacementViolations(app, p, hw, app.draw.formatDimension) : [];
    });

    // which face of the door the item mounts on (see itemFace), for a door placement.
    const onDoor = placements.some(p => p.target === "door");
    const face = item && onDoor ? faceLabel(itemFace(item)) : null;

    const alignX = placements[0]?.alignToX ?? "left";
    const alignY = placements[0]?.alignToY ?? "top";

    const setAlignX = (v: MdHardwarePlacement["alignToX"]) => {
        for(const p of placements) p.alignToX = v;
        app.hardware.listChanged.emit("align");
    };
    const setAlignY = (v: MdHardwarePlacement["alignToY"]) => {
        for(const p of placements) p.alignToY = v;
        app.hardware.listChanged.emit("align");
    };

    const xOptions = [
        {value: "left", label: "Left", icon: <AlignHorizontalLeftIcon />},
        {value: "right", label: "Right", icon: <AlignHorizontalRightIcon />},
        {value: "both", label: "Both", icon: <AlignHorizontalCenterIcon />},
    ] as const;
    const yOptions = [
        {value: "top", label: "Top", icon: <AlignVerticalTopIcon />},
        {value: "bottom", label: "Bottom", icon: <AlignVerticalBottomIcon />},
        {value: "both", label: "Both", icon: <AlignVerticalCenterIcon />},
    ] as const;

    // put each selected placement back at its standard location: a placement with a prep
    // back on it, with a 161 / DB anchor at its standard location; another door placement
    // at its category standard (see standardPosition); a frame placement recentres on its
    // stick (the crosshair auto-centres while unmodified).
    const reset = () => {
        const hw = app.hardwareGroup?.hardware ?? [];
        for(const p of placements) {
            const product = hw.find(h => h.product === p.productId);
            const link = app.hardware.linkFor(p);
            if(link) {
                if(!app.hardware.isLinked(p)) app.hardware.link(p);
                if(link === "161" || link === "db") app.hardware.resetAnchor(p.side, link);
            } else if(p.target === "door" && product) {
                const pos = standardPosition(app, p, product);
                p.x.userSet(pos.x, {debug: `${p.id}.reset`});
                p.y.userSet(pos.y, {debug: `${p.id}.reset`});
            } else {
                p.x.userClearModified();
                p.y.userClearModified();
            }
        }
        app.hardware.listChanged.emit("reset");
    };

    // the prep link: a placement sits on the prep its rule names (see MdHardware.prepAnchors)
    // or floats free. The "Linked to" dropdown offers that prep and "None"; it reads the prep
    // while any selected placement is linked. Shown while a placement is linked or can link.
    const linked = placements.filter(p => app.hardware.isLinked(p));
    const linkable = placements.filter(p => !app.hardware.isLinked(p) && app.hardware.canLink(p));
    const prepOptions = Array.from(new Set(placements.map(p => p.linkedTo ?? app.hardware.linkFor(p)).filter((k): k is PrepKind => !!k)));
    const linkedToOptions: {value: LinkedTo; label: string}[] = [
        ...prepOptions.map(k => ({value: k, label: PREP_LABELS[k]})),
        {value: "none", label: "None"},
    ];
    const linkedTo: LinkedTo = linked[0]?.linkedTo ?? "none";
    const setLinkedTo = (v: LinkedTo) => {
        if(v === "none") {
            for(const p of linked) app.hardware.unlink(p);
        } else {
            for(const p of linkable) app.hardware.link(p);
        }
    };

    // alignment picks which edge dimensions a frame crosshair shows; door hardware has none.
    const alignable = placements.length > 0 && placements.every(p => p.target === "frame");

    return (
        <>
            <RibbonGroup label="Item">
                <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", px: 1, minWidth: 120}}>
                    {item ? (
                        <>
                            <Typography sx={{pt: 1, fontSize: 13, fontWeight: 600, lineHeight: 1.3}}>
                                {rendered.map((r, i) => (
                                    <span key={r.id}>
                                        {i > 0 && ", "}
                                        <Link component="button" underline="hover" color="inherit"
                                              sx={{fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit", verticalAlign: "baseline"}}
                                              title="Edit product"
                                              onClick={() => setEditProduct(r.id)}>
                                            {r.productCode}
                                        </Link>
                                    </span>
                                ))}
                            </Typography>
                            <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}}>{item.category}</Typography>
                            <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}}>{item.manufacturerName}</Typography>
                        </>
                    ) : (
                        <Typography sx={{fontSize: 12, color: "text.secondary"}}>
                            {productIds.length} items
                        </Typography>
                    )}
                </Box>
            </RibbonGroup>

            {face && (
                <RibbonGroup label="Details">
                    <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", px: 1}}>
                        <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.3}}>Face</Typography>
                        <Typography sx={{fontSize: 13, lineHeight: 1.3}}>{face}</Typography>
                    </Box>
                </RibbonGroup>
            )}

            {editProduct !== null && (
                <ProductEditFromId id={editProduct} mode="hardware" onClose={() => setEditProduct(null)} />
            )}

            {violations.length > 0 && (
                <RibbonGroup label="Placement">
                    <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", px: 1, maxWidth: 320}}>
                        {violations.map((v, i) => (
                            <Typography key={i} sx={{fontSize: 11, color: "error.main", lineHeight: 1.3}}>
                                {v.category}: {v.message}
                            </Typography>
                        ))}
                    </Box>
                </RibbonGroup>
            )}

            {alignable && (
                <RibbonGroup label="Horizontal">
                    {xOptions.map(o => (
                        <RibbonButton
                            key={o.value}
                            icon={o.icon}
                            label={o.label}
                            active={alignX === o.value}
                            onClick={() => setAlignX(o.value)}
                        />
                    ))}
                </RibbonGroup>
            )}

            {alignable && (
                <RibbonGroup label="Vertical">
                    {yOptions.map(o => (
                        <RibbonButton
                            key={o.value}
                            icon={o.icon}
                            label={o.label}
                            active={alignY === o.value}
                            onClick={() => setAlignY(o.value)}
                        />
                    ))}
                </RibbonGroup>
            )}

            {(linked.length > 0 || linkable.length > 0) && (
                <RibbonGroup label="Linked to">
                    <RibbonSelect
                        value={linkedTo}
                        options={linkedToOptions}
                        onChange={setLinkedTo}
                    />
                </RibbonGroup>
            )}

            <RibbonGroup label="Actions">
                <RibbonButton
                    icon={<RestartAltIcon />}
                    label="Reset"
                    onClick={reset}
                />
                {workspace === "hardware" && (
                    <RibbonButton
                        icon={<TuneIcon />}
                        label="Manage"
                        onClick={() => app.viewSettings.hardwareSidebarCollapsed.emit(false)}
                    />
                )}
            </RibbonGroup>
        </>
    );
}

function faceLabel(face: DoorFace | null): string {
    switch(face) {
        case "push": return "Push side";
        case "pull": return "Pull side";
        default: return "Both";
    }
}

// the distinct products an item renders as on this opening, in order of first appearance,
// or the generic product when it has no dimensioned codes.
function renderedProducts(item: HardwareItem): {id: number; productCode: string}[] {
    const byDimension = item.calcByDimension ?? [];
    if(byDimension.length === 0) return [{id: item.product, productCode: item.productCode}];

    const out: {id: number; productCode: string}[] = [];
    for(const c of byDimension) {
        if(out.some(o => o.id === c.id)) continue;
        out.push({id: c.id, productCode: c.productCode});
    }
    return out;
}
